import { h } from '@atomify/jsx';

interface SelectOption {
    value: string;
    label: string | Text;
    rawValue?: string;
    default?: boolean;
    selected?: boolean;
}

interface SelectData {
    name: string;
    label: string | Text;
    id: string;
    options: SelectOption[];
    classes?: string;
    disabled?: boolean;
    readonly?: boolean;
    attr?: { [key: string]: string };
    validationTypes?: string;
    validationRegex?: string;
    validationErrorMessage?: string;
    requiredErrorMessage?: string;
    value?: string;
    sronly?: boolean;
    bpdInputHook?: string;
    hook?: string;
    required?: boolean;
    error?: string | Text;
    customSelect?: boolean;
    onOptionSelect?: (option: SelectOption) => void;
}

const SelectElement = (data: SelectData) => {
    const preselected = data.options.some(({ selected }) => selected);
    const classes: { [key: string]: boolean } = {
        ['c-select']: true,
        ['form__item']: true,
        ['floating-label-active']: true,
        ['form__item--disabled']: !!data.disabled,
        ...(data.classes && { [data.classes]: true }),
    };

    const Wrapper = data.customSelect ? 'bpd-select' : 'div';

    return (
        <Wrapper class={classes} {...data.attr} preselected={preselected}>
            <bpd-input
                validation-types={data.validationTypes}
                validation-regex={data.validationRegex}
                validation-error-message={data.validationErrorMessage}
                required-error-message={data.requiredErrorMessage}
                bind-value={data.value}
                {...{
                    [`js-hook-${data.bpdInputHook || ''}`]: !!data.bpdInputHook,
                }}>
                <div class="select__wrapper">
                    <label class={`select__label${data.sronly ? ' u-sr-only' : ''}`} for={data.id}>
                        {data.label}
                    </label>

                    <select
                        class="select__input"
                        name={data.name}
                        id={data.id}
                        {...{
                            required: data.required,
                            disabled: data.disabled,
                            readonly: data.readonly,
                            [`js-hook-${data.hook || ''}`]: !!data.hook,
                            ...data.attr,
                        }}>
                        {data.options.map((option) => (
                            <option
                                value={option.value}
                                {...{
                                    hidden: option.default,
                                    selected: option.default || option.selected,
                                }}
                                {...(option.rawValue && { ['data-raw']: option.rawValue })}>
                                {!option.default && option.label}
                            </option>
                        ))}
                    </select>

                    <span class="focus-helper"></span>
                </div>

                {data.customSelect ? (
                    <div class="select__dropdown" js-hook-select-dropdown>
                        <ul class="select__dropdown-list">
                            {data.options.map((option) => {
                                if (option.default) return null;
                                return (
                                    <button
                                        class="select__dropdown-item"
                                        js-hook-select-item
                                        data-value={option.value}
                                        data-label={option.label}
                                        {...(option.rawValue && { ['data-raw']: option.rawValue })}
                                        {...(data.onOptionSelect && {
                                            onclick: () => data.onOptionSelect!(option),
                                        })}>
                                        {option.label}
                                    </button>
                                );
                            })}
                        </ul>
                    </div>
                ) : null}

                <div class="form__item-error" role="alert">
                    {data.error}
                </div>
            </bpd-input>
        </Wrapper>
    );
};

export { SelectElement, SelectOption, SelectData };
